import { objectWithoutProperties as _objectWithoutProperties, extends as _extends, objectSpread2 as _objectSpread2 } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useRef, useCallback, useEffect } from 'react';
import { Transition } from 'react-transition-group';
import useForkRef from '../../../hooks/use-fork-ref.esm.js';
import { useSafeThemeUI } from '../../../theme-provider/use-theme-ui.esm.js';
import { duration } from '../../../themes/theme-keys/transitions.esm.js';
import { isNumber } from '../../../utils/assertion.esm.js';
import { reflow, getTransitionProps } from '../transitions/utils.esm.js';

var _excluded = ["appear", "children", "easing", "in", "onEnter", "onExit", "style", "timeout", "transitionStyles"];
function getScale(value) {
  return "scale(".concat(value, ", ").concat(Math.pow(value, 2), ")");
}
var styles = {
  entering: {
    opacity: 1,
    transform: getScale(1)
  },
  entered: {
    opacity: 1,
    transform: 'none'
  }
};

/**
 * The Grow transition is used by the [Tooltip](/story/components-tooltip--tooltip) and
 * [Popover](/components/popover/) components.
 * It uses [react-transition-group](https://github.com/reactjs/react-transition-group) internally.
 */
// no way around this ts-ignore that I can find compound pattern does not seem play well with React.forwardRef
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
var Grow = /*#__PURE__*/React.forwardRef(function Grow(props, ref) {
  var _props$appear = props.appear,
    appear = _props$appear === void 0 ? true : _props$appear,
    children = props.children,
    easing = props.easing,
    inProp = props["in"],
    onEnter = props.onEnter,
    onExit = props.onExit,
    style = props.style,
    _props$timeout = props.timeout,
    timeout = _props$timeout === void 0 ? duration.standard : _props$timeout,
    _props$transitionStyl = props.transitionStyles,
    transitionStyles = _props$transitionStyl === void 0 ? {} : _props$transitionStyl,
    other = _objectWithoutProperties(props, _excluded);
  var timer = useRef();
  var autoTimeout = useRef();
  var handleRef = children ? useForkRef(children.ref, ref) : useRef();
  var theme = useSafeThemeUI(props);
  var handleEnter = useCallback(function (node, isAppearing) {
    reflow(node); // So the animation always start from the start.

    var _getTransitionProps = getTransitionProps({
        style: style,
        timeout: timeout !== 'auto' ? timeout : duration.standard,
        easing: easing
      }, {
        mode: 'enter'
      }),
      transitionDuration = _getTransitionProps.duration,
      delay = _getTransitionProps.delay;
    var duration$1;
    if (timeout === 'auto') {
      duration$1 = theme.transitions ? theme.transitions.getAutoHeightDuration(node.clientHeight) : 500;
      autoTimeout.current = duration$1;
    } else {
      duration$1 = transitionDuration;
    }
    node.style.transition = [theme.transitions ? theme.transitions.create('opacity', {
      duration: duration$1,
      delay: delay
    }) : '', theme.transitions && isNumber(duration$1) ? theme.transitions.create('transform', {
      duration: duration$1 * 0.666,
      delay: delay
    }) : ''].join(',');
    if (onEnter) {
      onEnter(node, isAppearing);
    }
  }, [easing, onEnter, style, theme.transitions, timeout]);
  var handleExit = useCallback(function (node) {
    var _getTransitionProps2 = getTransitionProps({
        style: style,
        timeout: timeout !== 'auto' ? timeout : duration.standard,
        easing: easing
      }, {
        mode: 'exit'
      }),
      transitionDuration = _getTransitionProps2.duration,
      delay = _getTransitionProps2.delay;
    var duration$1;
    if (timeout === 'auto') {
      duration$1 = theme.transitions ? theme.transitions.getAutoHeightDuration(node.clientHeight) : 500;
      autoTimeout.current = duration$1;
    } else {
      duration$1 = transitionDuration;
    }
    node.style.transition = [theme.transitions ? theme.transitions.create('opacity', {
      duration: duration$1,
      delay: delay
    }) : '', theme.transitions && isNumber(duration$1) ? theme.transitions.create('transform', {
      duration: duration$1 * 0.666,
      delay: delay || duration$1 * 0.333
    }) : ''].join(',');
    node.style.opacity = '0';
    node.style.transform = getScale(0.75);
    if (onExit) {
      onExit(node);
    }
  }, [easing, onExit, style, theme.transitions, timeout]);
  var addEndListener = useCallback(function (_, next) {
    if (timeout === 'auto') {
      timer.current = setTimeout(next, autoTimeout.current || 0);
    }
  }, [timeout]);
  useEffect(function () {
    return function () {
      clearTimeout(timer.current);
    };
  }, []);
  return /*#__PURE__*/React.createElement(Transition, _extends({
    appear: appear,
    "in": inProp,
    onEnter: handleEnter,
    onExit: handleExit,
    addEndListener: addEndListener,
    timeout: timeout === 'auto' ? 500 : timeout
  }, other),
  // @ts-ignore
  function (state, childProps) {
    return children ? /*#__PURE__*/React.cloneElement(children, _objectSpread2({
      style: _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({
        opacity: 0,
        transform: getScale(0.75),
        visibility: state === 'exited' && !inProp ? 'hidden' : undefined
      }, styles[state]), transitionStyles[state]), style), children.props.style),
      ref: handleRef
    }, childProps)) : null;
  });
});
Grow.supportAuto = true;

export default Grow;
//# sourceMappingURL=grow.esm.js.map
